import { apiConfig } from '@nomiss/configs';
import {
  ApiRequestError,
  executeSdkEnvelopeRequest,
  executeSdkRawRequest,
  parseApiErrorResponse,
} from './api-request.util';
import {
  adminMonitoringApi,
  buildApiHeaders,
  fetchWithAuthRecovery,
} from './sdk-client';

export class AdminMonitoringError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminMonitoringError');
  }
}

export type AdminBrokenLinkStatus =
  | 'open'
  | 'in_progress'
  | 'resolved'
  | 'dismissed';

export type AdminBrokenLinksListParams = {
  status?: AdminBrokenLinkStatus;
  sportId?: string;
  page?: number;
  limit?: number;
};

export type AdminBrokenLinkListItem = {
  id: string;
  userId: string | null;
  reporterName: string | null;
  reporterEmail: string | null;
  digestId: string | null;
  highlightId: string | null;
  brokenLink: string;
  changedLink: string | null;
  sportName: string | null;
  platformName: string | null;
  status: AdminBrokenLinkStatus | string;
  reportedAt: string;
  createdAt: string;
};

export type AdminBrokenLinksListMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type AdminBrokenLinksListResult = {
  data: AdminBrokenLinkListItem[];
  meta: AdminBrokenLinksListMeta;
};

export type AdminBrokenLinkSportStat = {
  sport_id: string;
  sport_name: string;
  count: number;
};

export type AdminBrokenLinkStats = {
  total: number;
  by_status: Record<string, number>;
  by_sport: AdminBrokenLinkSportStat[];
  recent_7d: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseCount(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function parseListMeta(value: unknown): AdminBrokenLinksListMeta {
  if (!isRecord(value)) {
    return { page: 1, limit: 20, total: 0, totalPages: 0 };
  }

  return {
    page: parseCount(value.page, 1),
    limit: parseCount(value.limit, 20),
    total: parseCount(value.total, 0),
    totalPages: parseCount(value.totalPages, 0),
  };
}

function parseBrokenLinkItem(value: unknown): AdminBrokenLinkListItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  const createdAt = parseTimestamp(value.createdAt);
  const reportedAt = parseTimestamp(value.reportedAt) || createdAt;

  const sportName =
    typeof value.sportName === 'string'
      ? value.sportName
      : typeof value['sport_name'] === 'string'
        ? value['sport_name']
        : null;

  const platformName =
    typeof value.platformName === 'string'
      ? value.platformName
      : typeof value['platform_name'] === 'string'
        ? value['platform_name']
        : null;

  const highlightId =
    typeof value.highlightId === 'string'
      ? value.highlightId
      : typeof value['highlight_id'] === 'string'
        ? value['highlight_id']
        : null;

  const changedLink =
    typeof value.changedLink === 'string'
      ? value.changedLink
      : typeof value['changed_link'] === 'string'
        ? value['changed_link']
        : null;

  return {
    id: value.id,
    userId: typeof value.userId === 'string' ? value.userId : null,
    reporterName:
      typeof value.reporterName === 'string' ? value.reporterName : null,
    reporterEmail:
      typeof value.reporterEmail === 'string' ? value.reporterEmail : null,
    digestId: typeof value.digestId === 'string' ? value.digestId : null,
    highlightId,
    brokenLink: typeof value.brokenLink === 'string' ? value.brokenLink : '',
    changedLink,
    sportName,
    platformName,
    status: typeof value.status === 'string' ? value.status : 'open',
    reportedAt,
    createdAt,
  };
}

function parseTimestamp(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString();
  return '';
}

export async function listAdminBrokenLinks(
  params: AdminBrokenLinksListParams = {},
): Promise<AdminBrokenLinksListResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminMonitoringApi.adminMonitoringControllerListBrokenLinksV1Raw({
        ...(params.status ? { status: params.status } : {}),
        ...(params.sportId ? { sportId: params.sportId } : {}),
        ...(params.page != null ? { page: params.page } : {}),
        ...(params.limit != null ? { limit: params.limit } : {}),
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const rows = Array.isArray(envelope.data) ? envelope.data : [];
      return {
        data: rows
          .map(parseBrokenLinkItem)
          .filter((row): row is AdminBrokenLinkListItem => row != null),
        meta: parseListMeta(envelope.meta),
      };
    },
    fallback: 'Failed to load broken links',
    ErrorClass: AdminMonitoringError,
  });
}

export async function getAdminBrokenLinkStats(): Promise<AdminBrokenLinkStats> {
  return executeSdkRawRequest({
    request: () =>
      adminMonitoringApi.adminMonitoringControllerGetBrokenLinkStatsV1Raw(),
    fallback: 'Failed to load broken link stats',
    ErrorClass: AdminMonitoringError,
  });
}

export async function updateAdminBrokenLinkStatus(
  id: string,
  status: AdminBrokenLinkStatus,
): Promise<void> {
  await executeSdkRawRequest<unknown>({
    request: () =>
      adminMonitoringApi.adminMonitoringControllerUpdateBrokenLinkStatusV1Raw({
        id,
        status,
      }),
    fallback: 'Failed to update broken link status',
    ErrorClass: AdminMonitoringError,
  });
}

export type AdminSyncRunProviderStat = {
  provider_id: string;
  provider_name: string;
  total_runs: number;
};

export type AdminSyncRunStats = {
  by_status: Record<string, number>;
  by_provider: AdminSyncRunProviderStat[];
};

function parseSyncRunProviderStat(
  value: unknown,
): AdminSyncRunProviderStat | null {
  if (!isRecord(value) || typeof value.provider_id !== 'string') {
    return null;
  }

  const totalRuns =
    typeof value.total_runs === 'number'
      ? value.total_runs
      : typeof value.total_runs === 'string'
        ? Number(value.total_runs)
        : 0;

  return {
    provider_id: value.provider_id,
    provider_name:
      typeof value.provider_name === 'string' ? value.provider_name : '',
    total_runs: Number.isNaN(totalRuns) ? 0 : totalRuns,
  };
}

export type AdminCatalogSyncSport = {
  sportId: string;
  sportCode: string;
  sportName: string;
  syncEnabled: boolean;
  teamCount: number;
  playerCount: number;
  successRows: number;
  failedRows: number;
  lastSuccessAt: string | null;
  lastFailedAt: string | null;
};

export type AdminCatalogSyncTotals = {
  teamCount: number;
  playerCount: number;
  successRows: number;
  failedRows: number;
  lastSuccessAt: string | null;
  lastFailedAt: string | null;
};

export type AdminCatalogSync = {
  all: AdminCatalogSyncTotals;
  sports: AdminCatalogSyncSport[];
  queueBusy: boolean;
};

export type AdminCatalogSyncFailureItem = {
  id: string;
  sportCode: string;
  sportName: string;
  providerCode: string;
  ingestionType: 'team' | 'player' | 'match' | 'serp';
  leagueCode: string | null;
  leagueName: string | null;
  teamName: string | null;
  teamExternalId: string | null;
  reason: string;
  createdAt: string;
};

export type AdminCatalogSyncFailuresListParams = {
  page?: number;
  limit?: number;
};

export type AdminCatalogSyncFailuresListResult = {
  data: AdminCatalogSyncFailureItem[];
  meta: AdminBrokenLinksListMeta;
};

export type AdminMatchIngestFailuresListParams =
  AdminCatalogSyncFailuresListParams;
export type AdminMatchIngestFailuresListResult =
  AdminCatalogSyncFailuresListResult;
export type AdminSerpHighlightsFailuresListParams =
  AdminCatalogSyncFailuresListParams;
export type AdminSerpHighlightsFailuresListResult =
  AdminCatalogSyncFailuresListResult;

export type AdminMatchIngestSport = {
  sportId: string;
  sportCode: string;
  sportName: string;
  syncEnabled: boolean;
  lastSuccessAt: string | null;
  lastFailedAt: string | null;
};

export type AdminMatchIngestTotals = {
  lastSuccessAt: string | null;
  lastFailedAt: string | null;
};

export type AdminMatchIngest = {
  all: AdminMatchIngestTotals;
  sports: AdminMatchIngestSport[];
  queueBusy: boolean;
};

export type AdminMatchIngestLeague = {
  code: string;
  name: string;
};

export type AdminMatchIngestMatchParticipant = {
  label: string;
  role: string | null;
  score: number | null;
};

export type AdminMatchIngestHighlight = {
  id: string;
  title: string;
  url: string;
  thumbnailUrl: string | null;
};

export type AdminMatchIngestMatchItem = {
  id: string;
  name: string;
  matchDate: string;
  matchStatus: string;
  scoreLabel: string | null;
  leagueCode: string;
  leagueName: string;
  updatedAt: string;
  highlight: AdminMatchIngestHighlight | null;
  participants: AdminMatchIngestMatchParticipant[];
};

export type AdminMatchIngestMatchesListParams = {
  sportCode: string;
  status?: string;
  leagueCode?: string;
  from?: string;
  to?: string;
  hasHighlight?: boolean;
  page?: number;
  limit?: number;
};

export type AdminMatchIngestMatchesListResult = {
  data: AdminMatchIngestMatchItem[];
  meta: AdminBrokenLinksListMeta;
};

function parseCatalogSyncTotals(value: unknown): AdminCatalogSyncTotals {
  const data = isRecord(value) ? value : {};
  return {
    teamCount: parseCount(data.teamCount),
    playerCount: parseCount(data.playerCount),
    successRows: parseCount(data.successRows),
    failedRows: parseCount(data.failedRows),
    lastSuccessAt: parseNullableString(data.lastSuccessAt),
    lastFailedAt: parseNullableString(data.lastFailedAt),
  };
}

function parseCatalogSyncSport(value: unknown): AdminCatalogSyncSport | null {
  if (!isRecord(value) || typeof value.sportId !== 'string') {
    return null;
  }
  return {
    sportId: value.sportId,
    sportCode: typeof value.sportCode === 'string' ? value.sportCode : '',
    sportName: typeof value.sportName === 'string' ? value.sportName : '',
    syncEnabled: parseBooleanFlag(value.syncEnabled),
    teamCount: parseCount(value.teamCount),
    playerCount: parseCount(value.playerCount),
    successRows: parseCount(value.successRows),
    failedRows: parseCount(value.failedRows),
    lastSuccessAt: parseNullableString(value.lastSuccessAt),
    lastFailedAt: parseNullableString(value.lastFailedAt),
  };
}

function parseCatalogSyncFailureItem(
  value: unknown,
): AdminCatalogSyncFailureItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }
  return {
    id: value.id,
    sportCode: typeof value.sportCode === 'string' ? value.sportCode : '',
    sportName: typeof value.sportName === 'string' ? value.sportName : '',
    providerCode:
      typeof value.providerCode === 'string' ? value.providerCode : '',
    ingestionType:
      value.ingestionType === 'player'
        ? 'player'
        : value.ingestionType === 'match'
          ? 'match'
          : value.ingestionType === 'serp'
            ? 'serp'
            : 'team',
    leagueCode: parseNullableString(value.leagueCode),
    leagueName: parseNullableString(value.leagueName),
    teamName: parseNullableString(value.teamName),
    teamExternalId: parseNullableString(value.teamExternalId),
    reason: typeof value.reason === 'string' ? value.reason : '',
    createdAt: parseIsoDate(value.createdAt),
  };
}

export async function getAdminCatalogSync(): Promise<AdminCatalogSync> {
  const json = await fetchAdminMonitoringEnvelope(
    'catalog-sync',
    {},
    'Failed to load catalog sync',
  );
  const envelope = isRecord(json) ? json : {};
  const data = isRecord(envelope.data) ? envelope.data : {};
  const sports = Array.isArray(data.sports) ? data.sports : [];

  return {
    all: parseCatalogSyncTotals(data.all),
    sports: sports
      .map(parseCatalogSyncSport)
      .filter((row): row is AdminCatalogSyncSport => row != null),
    queueBusy: data.queueBusy === true,
  };
}

export async function getAdminCatalogSyncFailures(
  params: AdminCatalogSyncFailuresListParams = {},
): Promise<AdminCatalogSyncFailuresListResult> {
  return fetchProviderFailures(
    'catalog-sync/failures',
    params,
    'Failed to load catalog sync failures',
  );
}

export async function getAdminMatchIngestFailures(
  params: AdminMatchIngestFailuresListParams = {},
): Promise<AdminMatchIngestFailuresListResult> {
  return fetchProviderFailures(
    'match-ingest/failures',
    params,
    'Failed to load match ingest failures',
  );
}

export async function getAdminSerpHighlightsFailures(
  params: AdminSerpHighlightsFailuresListParams = {},
): Promise<AdminSerpHighlightsFailuresListResult> {
  return fetchProviderFailures(
    'serp-highlights/failures',
    params,
    'Failed to load OTT highlight failures',
  );
}

function parseMatchIngestTotals(value: unknown): AdminMatchIngestTotals {
  const data = isRecord(value) ? value : {};
  return {
    lastSuccessAt: parseNullableString(data.lastSuccessAt),
    lastFailedAt: parseNullableString(data.lastFailedAt),
  };
}

function parseMatchIngestSport(value: unknown): AdminMatchIngestSport | null {
  if (!isRecord(value) || typeof value.sportId !== 'string') {
    return null;
  }
  return {
    sportId: value.sportId,
    sportCode: typeof value.sportCode === 'string' ? value.sportCode : '',
    sportName: typeof value.sportName === 'string' ? value.sportName : '',
    syncEnabled: parseBooleanFlag(value.syncEnabled),
    lastSuccessAt: parseNullableString(value.lastSuccessAt),
    lastFailedAt: parseNullableString(value.lastFailedAt),
  };
}

export async function getAdminMatchIngest(): Promise<AdminMatchIngest> {
  const json = await fetchAdminMonitoringEnvelope(
    'match-ingest',
    {},
    'Failed to load match ingest',
  );
  const envelope = isRecord(json) ? json : {};
  const data = isRecord(envelope.data) ? envelope.data : {};
  const sports = Array.isArray(data.sports) ? data.sports : [];

  return {
    all: parseMatchIngestTotals(data.all),
    sports: sports
      .map(parseMatchIngestSport)
      .filter((row): row is AdminMatchIngestSport => row != null),
    queueBusy: data.queueBusy === true,
  };
}

export type AdminSerpHighlightsSport = AdminMatchIngestSport;
export type AdminSerpHighlightsTotals = AdminMatchIngestTotals;
export type AdminSerpHighlights = AdminMatchIngest & {
  queueBusy: boolean;
};

export async function getAdminSerpHighlights(): Promise<AdminSerpHighlights> {
  const json = await fetchAdminMonitoringEnvelope(
    'serp-highlights',
    {},
    'Failed to load OTT highlights',
  );
  const envelope = isRecord(json) ? json : {};
  const data = isRecord(envelope.data) ? envelope.data : {};
  const sports = Array.isArray(data.sports) ? data.sports : [];

  return {
    all: parseMatchIngestTotals(data.all),
    sports: sports
      .map(parseMatchIngestSport)
      .filter((row): row is AdminSerpHighlightsSport => row != null),
    queueBusy: data.queueBusy === true,
  };
}

function parseMatchIngestLeague(value: unknown): AdminMatchIngestLeague | null {
  if (!isRecord(value) || typeof value.code !== 'string') {
    return null;
  }
  return {
    code: value.code,
    name: typeof value.name === 'string' ? value.name : value.code,
  };
}

function parseMatchIngestMatchParticipant(
  value: unknown,
): AdminMatchIngestMatchParticipant | null {
  if (!isRecord(value) || typeof value.label !== 'string') {
    return null;
  }
  return {
    label: value.label,
    role: parseNullableString(value.role),
    score:
      typeof value.score === 'number' && Number.isFinite(value.score)
        ? value.score
        : null,
  };
}

function parseMatchIngestHighlight(
  value: unknown,
): AdminMatchIngestHighlight | null {
  if (!isRecord(value) || typeof value.url !== 'string' || value.url === '') {
    return null;
  }
  return {
    id: typeof value.id === 'string' ? value.id : '',
    title:
      typeof value.title === 'string' && value.title !== ''
        ? value.title
        : value.url,
    url: value.url,
    thumbnailUrl: parseNullableString(value.thumbnailUrl),
  };
}

function parseMatchIngestMatchItem(
  value: unknown,
): AdminMatchIngestMatchItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }
  const participants = Array.isArray(value.participants)
    ? value.participants
        .map(parseMatchIngestMatchParticipant)
        .filter((row): row is AdminMatchIngestMatchParticipant => row != null)
    : [];
  return {
    id: value.id,
    name: typeof value.name === 'string' ? value.name : 'Untitled match',
    matchDate: typeof value.matchDate === 'string' ? value.matchDate : '',
    matchStatus: typeof value.matchStatus === 'string' ? value.matchStatus : '',
    scoreLabel: parseNullableString(value.scoreLabel),
    leagueCode: typeof value.leagueCode === 'string' ? value.leagueCode : '',
    leagueName: typeof value.leagueName === 'string' ? value.leagueName : '',
    updatedAt: parseIsoDate(value.updatedAt),
    highlight: parseMatchIngestHighlight(value.highlight),
    participants,
  };
}

export async function getAdminMatchIngestLeagues(
  sportCode: string,
): Promise<AdminMatchIngestLeague[]> {
  const json = await fetchAdminMonitoringEnvelope(
    'match-ingest/leagues',
    { sportCode },
    'Failed to load match ingest leagues',
  );
  const envelope = isRecord(json) ? json : {};
  const rows = Array.isArray(envelope.data) ? envelope.data : [];
  return rows
    .map(parseMatchIngestLeague)
    .filter((row): row is AdminMatchIngestLeague => row != null);
}

export async function getAdminMatchIngestMatches(
  params: AdminMatchIngestMatchesListParams,
): Promise<AdminMatchIngestMatchesListResult> {
  const search: Record<string, string> = {
    sportCode: params.sportCode,
  };
  if (params.status != null && params.status !== '') {
    search.status = params.status;
  }
  if (params.leagueCode != null && params.leagueCode !== '') {
    search.leagueCode = params.leagueCode;
  }
  if (params.from != null && params.from !== '') {
    search.from = params.from;
  }
  if (params.to != null && params.to !== '') {
    search.to = params.to;
  }
  if (params.hasHighlight === true) {
    search.hasHighlight = 'true';
  } else if (params.hasHighlight === false) {
    search.hasHighlight = 'false';
  }
  if (params.page != null) {
    search.page = String(params.page);
  }
  if (params.limit != null) {
    search.limit = String(params.limit);
  }

  const json = await fetchAdminMonitoringEnvelope(
    'match-ingest/matches',
    search,
    'Failed to load ingested matches',
  );
  const envelope = isRecord(json) ? json : {};
  const rows = Array.isArray(envelope.data) ? envelope.data : [];
  return {
    data: rows
      .map(parseMatchIngestMatchItem)
      .filter((row): row is AdminMatchIngestMatchItem => row != null),
    meta: parseListMeta(envelope.meta),
  };
}

async function fetchProviderFailures(
  path: string,
  params: AdminCatalogSyncFailuresListParams,
  fallback: string,
): Promise<AdminCatalogSyncFailuresListResult> {
  const search: Record<string, string> = {};
  if (params.page != null) {
    search.page = String(params.page);
  }
  if (params.limit != null) {
    search.limit = String(params.limit);
  }

  const json = await fetchAdminMonitoringEnvelope(path, search, fallback);
  const envelope = isRecord(json) ? json : {};
  const rows = Array.isArray(envelope.data) ? envelope.data : [];
  return {
    data: rows
      .map(parseCatalogSyncFailureItem)
      .filter((row): row is AdminCatalogSyncFailureItem => row != null),
    meta: parseListMeta(envelope.meta),
  };
}

export async function getAdminSyncRunStats(): Promise<AdminSyncRunStats> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminMonitoringApi.adminMonitoringControllerGetSyncRunStatsV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : {};
      const byStatus = isRecord(data.by_status) ? data.by_status : {};
      const providers = Array.isArray(data.by_provider) ? data.by_provider : [];

      return {
        by_status: normalizeCountRecord(byStatus),
        by_provider: providers
          .map(parseSyncRunProviderStat)
          .filter((row): row is AdminSyncRunProviderStat => row != null),
      };
    },
    fallback: 'Failed to load sync run stats',
    ErrorClass: AdminMonitoringError,
  });
}

export type AdminDigestJobStats = {
  by_status: Record<string, number>;
  failed_last_7d: number;
};

export type AdminDigestDeliveryStats = {
  window_days: number;
  expected_jobs: number;
  notifications_sent: number;
  digests_read: number;
  delivery_rate: number;
};

function normalizeCountRecord(
  value: Record<string, unknown>,
): Record<string, number> {
  const normalized: Record<string, number> = {};
  for (const [key, entry] of Object.entries(value)) {
    normalized[key] = parseCount(entry);
  }
  return normalized;
}

export async function getAdminDigestJobStats(): Promise<AdminDigestJobStats> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminMonitoringApi.adminMonitoringControllerGetDigestJobStatsV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : {};
      const byStatus = isRecord(data.by_status) ? data.by_status : {};

      return {
        by_status: normalizeCountRecord(byStatus),
        failed_last_7d: parseCount(data.failed_last_7d),
      };
    },
    fallback: 'Failed to load digest job stats',
    ErrorClass: AdminMonitoringError,
  });
}

export async function getAdminDigestDeliveryStats(
  days = 7,
): Promise<AdminDigestDeliveryStats> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminMonitoringApi.adminMonitoringControllerGetDeliveryStatsV1Raw({
        days,
      }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : {};

      return {
        window_days: parseCount(data.window_days) || days,
        expected_jobs: parseCount(data.expected_jobs),
        notifications_sent: parseCount(data.notifications_sent),
        digests_read: parseCount(data.digests_read),
        delivery_rate: parseCount(data.delivery_rate),
      };
    },
    fallback: 'Failed to load digest delivery stats',
    ErrorClass: AdminMonitoringError,
  });
}

export type AdminContentHealthOverallStatus =
  | 'healthy'
  | 'degraded'
  | 'unhealthy';

export type AdminContentHealthQueueCounts = {
  waiting: number;
  inFlight: number;
  delayed: number;
};

export type AdminContentHealthQueue = {
  queueName: string;
  counts: AdminContentHealthQueueCounts;
  dlq: AdminContentHealthQueueCounts;
  retry: {
    visibilityTimeoutSeconds: number | null;
    maxReceiveCount: number | null;
    redrivePolicy: string | null;
  };
};

export type AdminContentHealthProvider = {
  sportCode: string;
  providerCode: string;
  lastSuccessfulSyncAt: string | null;
  stale: boolean;
  reachable?: boolean;
  latencyMs?: number;
  error?: string;
};

export type AdminContentHealthScrapers = {
  serp: { configured: boolean; reachable: boolean | null };
  youtube: {
    configured: boolean;
    pipelineEnabled: boolean;
    reachable: boolean | null;
  };
};

export type AdminContentHealthYoutubeStats = {
  lowScoreCount: number;
  rejectedCandidateCount: number;
  inBackoffCount: number;
  givenUpCount: number;
  missingChannelIdCount: number;
  leaguesWithoutMappingCount: number;
};

export type AdminContentHealthSnapshot = {
  overallStatus: AdminContentHealthOverallStatus;
  checkedAt: string;
  probed: boolean;
  catalog: {
    staleSports: string[];
    failedRuns7d: number;
    partialRuns7d: number;
  };
  queues: AdminContentHealthQueue[];
  providers: AdminContentHealthProvider[];
  scrapers: AdminContentHealthScrapers;
  youtube: AdminContentHealthYoutubeStats;
};

export type AdminYoutubeHighlightsListParams = {
  maxScore?: number;
  hasRejected?: boolean;
  page?: number;
  limit?: number;
};

export type AdminYoutubeHighlightListItem = {
  id: string;
  digestId: string | null;
  competitionId: string | null;
  url: string;
  title: string | null;
  selectionScore: number | null;
  rejectedCandidates: unknown[];
  channelsTried: unknown[];
  sportCode: string | null;
  leagueCode: string | null;
  competitionName: string | null;
  createdAt: string;
};

export type AdminYoutubeHighlightsListResult = {
  data: AdminYoutubeHighlightListItem[];
  meta: AdminBrokenLinksListMeta;
};

export type AdminYoutubeScrapeAttemptsListParams = {
  givenUp?: boolean;
  inBackoff?: boolean;
  page?: number;
  limit?: number;
};

export type AdminYoutubeScrapeAttemptListItem = {
  competitionId: string;
  attempts: number;
  lastAttemptedAt: string | null;
  nextAttemptAt: string | null;
  lastFailureReason: string | null;
  competitionName: string | null;
  leagueCode: string | null;
  sportCode: string | null;
};

export type AdminYoutubeScrapeAttemptsListResult = {
  data: AdminYoutubeScrapeAttemptListItem[];
  meta: AdminBrokenLinksListMeta;
};

export type AdminYoutubeChannelGapItem = {
  id: string;
  code: string;
  name: string;
  channelUrl?: string | null;
};

export type AdminYoutubeChannelGaps = {
  missingChannelIds: AdminYoutubeChannelGapItem[];
  leaguesWithoutMapping: AdminYoutubeChannelGapItem[];
};

function parseIsoDate(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return '';
}

function parseNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function parseBooleanFlag(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function parseQueueCounts(value: unknown): AdminContentHealthQueueCounts {
  if (!isRecord(value)) {
    return { waiting: 0, inFlight: 0, delayed: 0 };
  }
  return {
    waiting: parseCount(value.waiting),
    inFlight: parseCount(value.inFlight),
    delayed: parseCount(value.delayed),
  };
}

function parseQueue(value: unknown): AdminContentHealthQueue | null {
  if (!isRecord(value) || typeof value.queueName !== 'string') {
    return null;
  }
  const retry = isRecord(value.retry) ? value.retry : {};
  return {
    queueName: value.queueName,
    counts: parseQueueCounts(value.counts),
    dlq: parseQueueCounts(value.dlq),
    retry: {
      visibilityTimeoutSeconds:
        typeof retry.visibilityTimeoutSeconds === 'number'
          ? retry.visibilityTimeoutSeconds
          : null,
      maxReceiveCount:
        typeof retry.maxReceiveCount === 'number'
          ? retry.maxReceiveCount
          : null,
      redrivePolicy:
        typeof retry.redrivePolicy === 'string' ? retry.redrivePolicy : null,
    },
  };
}

function parseProvider(value: unknown): AdminContentHealthProvider | null {
  if (!isRecord(value) || typeof value.sportCode !== 'string') {
    return null;
  }
  const row: AdminContentHealthProvider = {
    sportCode: value.sportCode,
    providerCode:
      typeof value.providerCode === 'string' ? value.providerCode : '',
    lastSuccessfulSyncAt: parseNullableString(value.lastSuccessfulSyncAt),
    stale: parseBooleanFlag(value.stale),
  };
  if (typeof value.reachable === 'boolean') {
    row.reachable = value.reachable;
  }
  if (typeof value.latencyMs === 'number') {
    row.latencyMs = value.latencyMs;
  }
  if (typeof value.error === 'string') {
    row.error = value.error;
  }
  return row;
}

function parseScrapers(value: unknown): AdminContentHealthScrapers {
  const scrapers = isRecord(value) ? value : {};
  const serp = isRecord(scrapers.serp) ? scrapers.serp : {};
  const youtube = isRecord(scrapers.youtube) ? scrapers.youtube : {};
  return {
    serp: {
      configured: parseBooleanFlag(serp.configured),
      reachable: typeof serp.reachable === 'boolean' ? serp.reachable : null,
    },
    youtube: {
      configured: parseBooleanFlag(youtube.configured),
      pipelineEnabled: parseBooleanFlag(youtube.pipelineEnabled),
      reachable:
        typeof youtube.reachable === 'boolean' ? youtube.reachable : null,
    },
  };
}

function parseYoutubeStats(value: unknown): AdminContentHealthYoutubeStats {
  const stats = isRecord(value) ? value : {};
  return {
    lowScoreCount: parseCount(stats.lowScoreCount),
    rejectedCandidateCount: parseCount(stats.rejectedCandidateCount),
    inBackoffCount: parseCount(stats.inBackoffCount),
    givenUpCount: parseCount(stats.givenUpCount),
    missingChannelIdCount: parseCount(stats.missingChannelIdCount),
    leaguesWithoutMappingCount: parseCount(stats.leaguesWithoutMappingCount),
  };
}

function parseOverallStatus(value: unknown): AdminContentHealthOverallStatus {
  if (value === 'healthy' || value === 'degraded' || value === 'unhealthy') {
    return value;
  }
  return 'unhealthy';
}

function parseContentHealthSnapshot(
  value: unknown,
): AdminContentHealthSnapshot {
  const data = isRecord(value) ? value : {};
  const catalog = isRecord(data.catalog) ? data.catalog : {};
  const staleSports = Array.isArray(catalog.staleSports)
    ? catalog.staleSports.filter(
        (entry): entry is string => typeof entry === 'string',
      )
    : [];
  const queues = Array.isArray(data.queues) ? data.queues : [];
  const providers = Array.isArray(data.providers) ? data.providers : [];

  return {
    overallStatus: parseOverallStatus(data.overallStatus),
    checkedAt: parseIsoDate(data.checkedAt),
    probed: parseBooleanFlag(data.probed),
    catalog: {
      staleSports,
      failedRuns7d: parseCount(catalog.failedRuns7d),
      partialRuns7d: parseCount(catalog.partialRuns7d),
    },
    queues: queues
      .map(parseQueue)
      .filter((row): row is AdminContentHealthQueue => row != null),
    providers: providers
      .map(parseProvider)
      .filter((row): row is AdminContentHealthProvider => row != null),
    scrapers: parseScrapers(data.scrapers),
    youtube: parseYoutubeStats(data.youtube),
  };
}

function parseYoutubeHighlightItem(
  value: unknown,
): AdminYoutubeHighlightListItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }
  return {
    id: value.id,
    digestId: parseNullableString(value.digestId),
    competitionId: parseNullableString(value.competitionId),
    url: typeof value.url === 'string' ? value.url : '',
    title: parseNullableString(value.title),
    selectionScore:
      typeof value.selectionScore === 'number' ? value.selectionScore : null,
    rejectedCandidates: Array.isArray(value.rejectedCandidates)
      ? value.rejectedCandidates
      : [],
    channelsTried: Array.isArray(value.channelsTried)
      ? value.channelsTried
      : [],
    sportCode: parseNullableString(value.sportCode),
    leagueCode: parseNullableString(value.leagueCode),
    competitionName: parseNullableString(value.competitionName),
    createdAt: parseIsoDate(value.createdAt),
  };
}

function parseYoutubeScrapeAttemptItem(
  value: unknown,
): AdminYoutubeScrapeAttemptListItem | null {
  if (!isRecord(value) || typeof value.competitionId !== 'string') {
    return null;
  }
  return {
    competitionId: value.competitionId,
    attempts: parseCount(value.attempts),
    lastAttemptedAt: parseIsoDate(value.lastAttemptedAt) || null,
    nextAttemptAt: parseIsoDate(value.nextAttemptAt) || null,
    lastFailureReason: parseNullableString(value.lastFailureReason),
    competitionName: parseNullableString(value.competitionName),
    leagueCode: parseNullableString(value.leagueCode),
    sportCode: parseNullableString(value.sportCode),
  };
}

function parseYoutubeChannelGapItem(
  value: unknown,
): AdminYoutubeChannelGapItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }
  const item: AdminYoutubeChannelGapItem = {
    id: value.id,
    code: typeof value.code === 'string' ? value.code : '',
    name: typeof value.name === 'string' ? value.name : '',
  };
  if (typeof value.channelUrl === 'string') {
    item.channelUrl = value.channelUrl;
  } else if (value.channelUrl === null) {
    item.channelUrl = null;
  }
  return item;
}

async function fetchAdminMonitoringEnvelope(
  path: string,
  search: Record<string, string>,
  fallback: string,
): Promise<unknown> {
  const url = new URL(`${apiConfig.baseUrl}/v1/admin/monitoring/${path}`);
  for (const [key, value] of Object.entries(search)) {
    url.searchParams.set(key, value);
  }

  const response = await fetchWithAuthRecovery(url.toString(), {
    method: 'GET',
    headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
  });

  if (!response.ok) {
    throw await parseApiErrorResponse(response, fallback, AdminMonitoringError);
  }

  return response.json();
}

export async function getAdminContentHealthSnapshot(
  params: {
    probe?: boolean;
  } = {},
): Promise<AdminContentHealthSnapshot> {
  const search: Record<string, string> = {};
  if (params.probe === true) {
    search.probe = 'true';
  }

  const json = await fetchAdminMonitoringEnvelope(
    'content-health',
    search,
    'Failed to load content health',
  );
  const envelope = isRecord(json) ? json : {};
  return parseContentHealthSnapshot(envelope.data);
}

export async function listAdminYoutubeHighlights(
  params: AdminYoutubeHighlightsListParams = {},
): Promise<AdminYoutubeHighlightsListResult> {
  const search: Record<string, string> = {};
  if (params.maxScore != null) {
    search.maxScore = String(params.maxScore);
  }
  if (params.hasRejected !== undefined) {
    search.hasRejected = params.hasRejected ? 'true' : 'false';
  }
  if (params.page != null) {
    search.page = String(params.page);
  }
  if (params.limit != null) {
    search.limit = String(params.limit);
  }

  const json = await fetchAdminMonitoringEnvelope(
    'youtube/highlights',
    search,
    'Failed to load YouTube highlights',
  );
  const envelope = isRecord(json) ? json : {};
  const rows = Array.isArray(envelope.data) ? envelope.data : [];
  return {
    data: rows
      .map(parseYoutubeHighlightItem)
      .filter((row): row is AdminYoutubeHighlightListItem => row != null),
    meta: parseListMeta(envelope.meta),
  };
}

export async function listAdminYoutubeScrapeAttempts(
  params: AdminYoutubeScrapeAttemptsListParams = {},
): Promise<AdminYoutubeScrapeAttemptsListResult> {
  const search: Record<string, string> = {};
  if (params.givenUp !== undefined) {
    search.givenUp = params.givenUp ? 'true' : 'false';
  }
  if (params.inBackoff !== undefined) {
    search.inBackoff = params.inBackoff ? 'true' : 'false';
  }
  if (params.page != null) {
    search.page = String(params.page);
  }
  if (params.limit != null) {
    search.limit = String(params.limit);
  }

  const json = await fetchAdminMonitoringEnvelope(
    'youtube/scrape-attempts',
    search,
    'Failed to load YouTube scrape attempts',
  );
  const envelope = isRecord(json) ? json : {};
  const rows = Array.isArray(envelope.data) ? envelope.data : [];
  return {
    data: rows
      .map(parseYoutubeScrapeAttemptItem)
      .filter((row): row is AdminYoutubeScrapeAttemptListItem => row != null),
    meta: parseListMeta(envelope.meta),
  };
}

export async function getAdminYoutubeChannelGaps(): Promise<AdminYoutubeChannelGaps> {
  const json = await fetchAdminMonitoringEnvelope(
    'youtube/channel-gaps',
    {},
    'Failed to load YouTube channel gaps',
  );
  const envelope = isRecord(json) ? json : {};
  const data = isRecord(envelope.data) ? envelope.data : {};
  const missing = Array.isArray(data.missingChannelIds)
    ? data.missingChannelIds
    : [];
  const unmapped = Array.isArray(data.leaguesWithoutMapping)
    ? data.leaguesWithoutMapping
    : [];

  return {
    missingChannelIds: missing
      .map(parseYoutubeChannelGapItem)
      .filter((row): row is AdminYoutubeChannelGapItem => row != null),
    leaguesWithoutMapping: unmapped
      .map(parseYoutubeChannelGapItem)
      .filter((row): row is AdminYoutubeChannelGapItem => row != null),
  };
}
